import Autocomplete from '@mui/material/Autocomplete';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import { ActivityTemplate } from 'common/models/Templates/ActivityTemplate';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GetActivityTemplateById,
  GetMyActivityTemplatesByEntityAndType,
} from 'services/TemplatesService';

interface Props {
  value: number;
  entityId: number;
  activityTypeId: number;
  label?: string;
  selectHandler: (item: ActivityTemplate | null) => void;
  errorHandler?: (message: string) => void;
}

export default function ActivityTemplatePicker({
  value,
  entityId,
  activityTypeId,
  label = 'Template',
  selectHandler,
  errorHandler,
}: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<ActivityTemplate[]>([]);

  useEffect(() => {
    const getOptions = async () => {
      const res = await GetMyActivityTemplatesByEntityAndType(
        entityId,
        activityTypeId,
        true,
        errorHandler
      );
      if (res) setOptions(res);
    };
    getOptions();
  }, [entityId, activityTypeId, errorHandler]);

  const selectedOption = useMemo(() => {
    if (value) {
      const val = options.find((o) => o.id === value);
      if (val) return val;
    }
    return null;
  }, [options, value]);

  const localSelectHandler = useCallback(
    async (event: any, template: ActivityTemplate | null) => {
      if (template === null || Boolean(template.text)) selectHandler(template);
      else {
        if (template.id <= 0) selectHandler(template);
        setIsLoading(true);
        const res = await GetActivityTemplateById(template.id, errorHandler);
        setIsLoading(false);
        if (res) {
          setOptions((prev) => {
            const tmp = [...prev];
            const item = tmp.find((v) => v.id === template.id);
            if (item) item.text = res.text;
            return tmp;
          });
          selectHandler(res);
        }
      }
    },
    [selectHandler, errorHandler]
  );

  return (
    <>
      <Autocomplete
        value={selectedOption}
        size="small"
        options={options}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        onChange={localSelectHandler}
        loading={isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            slotProps={{ htmlInput: { ...params.inputProps, 'data-lpignore': 'true' } }}
          />
        )}
        renderOption={(props, option) => (
          <ListItem {...props} key={option.id}>
            <ListItemText primary={option.name} secondary={option.ownerName} />
          </ListItem>
        )}
      />
    </>
  );
}
